import { Layout } from '../components/layout';
import { SEO } from '../components/SEO';

const HeroSection = () => {
  return (
    <section 
      className="min-h-[25vh] flex items-center bg-[#faf9f7]"
      data-testid="privacy-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-4 animate-fade-in-up"
          data-testid="privacy-hero-title"
        >
          Privacy Policy
        </h1>
        <p 
          className="font-dm-sans text-base text-[#4a4744] animate-fade-in-up animate-delay-200"
        >
          Last updated: May 2026
        </p>
      </div>
    </section>
  );
};

const PrivacyContent = () => {
  return (
    <section className="py-16 md:py-20 bg-white" data-testid="privacy-content">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            This Privacy Policy explains how Agilitie collects, uses, discloses and protects your personal information, and how you can access or correct that information or make a complaint. We are committed to handling your personal information openly and responsibly.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            Agilitie is the trading name of an Australian private company (ACN 631 628 834), registered for GST and based in Victoria, Australia. The company is responsible for the personal information handled under this policy.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            In this policy, <strong className="text-[#1a1a1a]">"we", "us", "our" and "Agilitie"</strong> refer to the company that operates the Agilitie business and website at agilitie.com.au. <strong className="text-[#1a1a1a]">"You"</strong> refers to any individual whose personal information we hold, including website visitors, prospective and current clients, and the people we work with to deliver our services.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            We handle personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). Where this policy refers to personal information, that includes sensitive information, which we treat with an even higher level of care.
          </p>

          {/* Section 1 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            1. What personal information we collect
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            Agilitie is a business-to-business advisory firm. We collect only the personal information we reasonably need to respond to enquiries, provide our services, and run our business. The information we collect depends on how you interact with us.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-4">
            <strong className="text-[#1a1a1a]">Website visitors and enquirers.</strong> When you contact us through our website, by email, or through our social media channels, we typically collect your name, your email address, your organisation and role, and the content of your message.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-4">
            <strong className="text-[#1a1a1a]">Clients and prospective clients.</strong> When we discuss or deliver an engagement, we may collect business contact details, information about your organisation and its people relevant to the work, and records of our communications and the services we provide.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            <strong className="text-[#1a1a1a]">People we work with.</strong> When we engage associates, contractors or specialists, we may collect contact details, professional experience, qualifications, and information needed to manage that working relationship.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We do not seek to collect sensitive information (such as information about your health, racial or ethnic origin, religious beliefs, or criminal record) in the ordinary course of our business. If we ever need to collect sensitive information, we will ask for your consent first, unless the law allows otherwise.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            If you choose not to provide information we reasonably need, we may be unable to respond to your enquiry, provide our services, or work with you effectively.
          </p>

          {/* Section 2 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            2. How we collect your personal information
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We usually collect personal information directly from you, for example when you complete a form on our website, send us an email, speak with us, or enter into an engagement with us.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            Sometimes we may collect information about you from third parties, such as a colleague who refers you, a referee you have nominated, or publicly available professional sources such as LinkedIn. We only do this where you would reasonably expect it, where you have consented, or where the law permits it. If we receive personal information we did not ask for and could not lawfully have collected, we will destroy or de-identify it where it is appropriate to do so.
          </p>

          {/* Section 3 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            3. Our website, analytics and cookies
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            When you visit agilitie.com.au, our website and its service providers may automatically record limited technical information that does not by itself identify you, such as your device or browser type, your approximate location, the pages you view, and the date and time of your visit. We use this information to understand how our website is used and to improve it.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            A cookie is a small data file stored on your device that helps a website function and remember your preferences. Our website may use cookies and similar technologies for essential functionality and for analytics. You can set your browser to refuse some or all cookies, or to alert you when sites set them, although some parts of the website may not work properly if you do.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            We use <strong className="text-[#1a1a1a]">Google Analytics</strong> to understand how visitors use our website. Google Analytics sets cookies and collects usage information such as the pages you view, how you arrived at our site, and your approximate location. This information is collected by Google on our behalf and may be stored and processed on servers outside Australia. You can opt out of Google Analytics through your browser settings or by installing Google's opt-out browser add-on.
          </p>

          {/* Section 4 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            4. How we use your personal information
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We use your personal information for the purpose for which it was collected and for related purposes you would reasonably expect. In practice, this means we use it to:
          </p>
          
          <ul className="list-disc list-outside ml-6 mb-6 space-y-2">
            <li className="text-[#4a4744] text-base leading-relaxed">respond to your enquiries and communicate with you;</li>
            <li className="text-[#4a4744] text-base leading-relaxed">provide, manage and improve our advisory services;</li>
            <li className="text-[#4a4744] text-base leading-relaxed">manage our relationships with clients, associates and contractors;</li>
            <li className="text-[#4a4744] text-base leading-relaxed">send you information about our work, insights or events where you would expect it or have asked to receive it;</li>
            <li className="text-[#4a4744] text-base leading-relaxed">run, protect and improve our business, including record keeping, quality, security and meeting our legal and insurance obligations.</li>
          </ul>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We will only use your personal information for an unrelated purpose if you have consented, or if we are required or authorised to do so by law.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            <strong className="text-[#1a1a1a]">Direct marketing.</strong> If we send you marketing communications, every message will include a simple way to opt out, and we will stop when you ask us to. We do not sell your personal information.
          </p>

          {/* Section 5 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            5. When we disclose your personal information
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We do not sell or rent your personal information. We may disclose it in limited circumstances, namely to:
          </p>
          
          <ul className="list-disc list-outside ml-6 mb-10 space-y-2">
            <li className="text-[#4a4744] text-base leading-relaxed">service providers who help us run our business and website, such as IT, hosting, email, analytics and professional advisers, who are only permitted to use it to provide their service to us;</li>
            <li className="text-[#4a4744] text-base leading-relaxed">anyone you have authorised us to share it with;</li>
            <li className="text-[#4a4744] text-base leading-relaxed">a buyer or successor if our business is restructured, sold or transferred; and</li>
            <li className="text-[#4a4744] text-base leading-relaxed">a person or authority where we are required or authorised by law to disclose it.</li>
          </ul>

          {/* Section 6 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            6. Overseas disclosure
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            Some of our service providers may store or process information outside Australia, for example where a hosting or software provider operates servers overseas. Where this happens, we take reasonable steps to ensure the information is handled consistently with the Australian Privacy Principles. By providing your personal information to us, you acknowledge that it may be stored or processed overseas by our service providers in this way.
          </p>

          {/* Section 7 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            7. Storage and security
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We take reasonable technical and organisational steps to protect your personal information from misuse, interference, loss, and unauthorised access, modification or disclosure. These steps include access controls, secure systems, and limiting the people who can access your information to those who need it.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            No method of transmission over the internet or electronic storage is completely secure, so we cannot guarantee absolute security. If a data breach occurs that is likely to cause you serious harm, we will respond in line with the Notifiable Data Breaches scheme, which may include notifying you and the Office of the Australian Information Commissioner.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            We keep personal information only for as long as we need it for the purposes described in this policy or to meet our legal obligations. When it is no longer needed, we take reasonable steps to destroy or de-identify it.
          </p>

          {/* Section 8 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            8. Accessing, correcting and deleting your information
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            You can ask us to access the personal information we hold about you, and to correct it if it is inaccurate, out of date or incomplete. You can also ask us to delete personal information we hold about you, and we will do so where we are able to and where the law does not require us to keep it.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            To make a request, contact us using the details below. We may need to verify your identity first. We will respond within a reasonable time, and access is free except in limited cases where a reasonable cost may apply. If we cannot give you access or make a change you have asked for, we will explain why.
          </p>

          {/* Section 9 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            9. Dealing with us anonymously
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            Where it is lawful and practicable, you have the option of dealing with us anonymously or using a pseudonym, for example when making a general enquiry. In most cases, however, we will need your contact details in order to respond or to provide our services.
          </p>

          {/* Section 10 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            10. Contact us and how to complain
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            If you have a question about this policy, want to access, correct or delete your information, or wish to make a privacy complaint, please contact us:
          </p>
          
          <div className="bg-[#faf9f7] border-l-4 border-[#d4782c] p-6 mb-6">
            <p className="text-[#1a1a1a] font-semibold mb-2">Privacy Officer, Agilitie</p>
            <p className="text-[#4a4744]">
              Email: <a href="mailto:contact@agilitie.com.au" className="text-[#d4782c] hover:underline">contact@agilitie.com.au</a>
            </p>
          </div>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-6">
            We take privacy complaints seriously and will handle them fairly and promptly. We will acknowledge your complaint, look into it, and respond to you in writing within a reasonable time.
          </p>
          
          <p className="text-[#4a4744] text-base leading-relaxed mb-10">
            If you are not satisfied with our response, you can contact the Office of the Australian Information Commissioner (OAIC) at{' '}
            <a 
              href="https://www.oaic.gov.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#d4782c] hover:underline"
            >
              www.oaic.gov.au
            </a>
            , by phone on 1300 363 992, or by writing to GPO Box 5288, Sydney NSW 2001.
          </p>

          {/* Section 11 */}
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mt-12 mb-6">
            11. Changes to this policy
          </h2>
          
          <p className="text-[#4a4744] text-base leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our practices or the law. The current version will always be available on our website, and the date at the top shows when it was last updated.
          </p>
        </div>
      </div>
    </section>
  );
};

export default function PrivacyPage() {
  return (
    <Layout>
      <SEO 
        title="Privacy Policy"
        path="/privacy"
        description="Agilitie's Privacy Policy explains how we collect, use, disclose and protect your personal information in accordance with the Privacy Act 1988 and Australian Privacy Principles."
      />
      <HeroSection />
      <PrivacyContent />
    </Layout>
  );
}
